<template>
  <div id="app">
    <component :is="layoutComponent">
      <!-- 라우트 레코드가 바뀌면 새 인스턴스다 — 같은 컴포넌트를 쓰는 두 라우트의 상태가 섞이지 않게([routeViewKey]) -->
      <router-view v-if="routeAllowed" :key="viewKey" />
      <!--
        세션 메뉴를 기다리는 화면 — 셸은 이미 그려져 있고 본문만 보류한다(R-L 리뷰 M-1). 본문을 만들면 그 화면의 첫 조회가
        나가는데, 메뉴에 없는 화면이면 403 = 강제 로그아웃이다. 메뉴가 오면 열리거나 안내로 바뀐다(`router`).
      -->
      <div v-else-if="routePending" v-loading="true" class="route-pending" data-testid="route-pending"></div>
    </component>
    <LoadingProgress />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import DefaultLayout from './components/layouts/DefaultLayout.vue'
import ClearLayout from './components/layouts/ClearLayout.vue'
import EmbedLayout from './components/layouts/EmbedLayout.vue'
import LoadingProgress from './components/layouts/common/LoadingProgress.vue'
import { isPublicRoute } from '@/utils/routeAccess'
import { ROUTE_LAYOUT, isEmbedPath, isEmbedRoute, routeLayoutOf, routeViewKey } from '@/utils/routeLayout'

export default {
  name: 'App',

  components: {
    LoadingProgress,
  },

  data() {
    return {}
  },
  computed: {
    ...mapGetters({
      isLogin: 'auth/isLogin',
      auth: 'auth/auth',
      authId: 'auth/authId',
    }),
    /**
     * POS 안에 띄우는 임베드 라우트인가 (`utils/routeLayout`).
     *
     * 이 라우트에는 앱 셸이 없다 — 주문 알림 웹소켓·`/login` 리다이렉트가 전부 여기서 갈린다. 동의·공지 대화상자와
     * 알림음은 DefaultLayout 안에 있어 `EmbedLayout` 이 자연히 빼놓는다.
     */
    isEmbed() {
      // 라우트가 아직 정해지지 않았으면(START) `meta` 가 비어 있어 주소로 판정한다 — 그러지 않으면
      // 최초 내비게이션이 실패한 임베드 창에 관리자 셸이 그려진다(2026-09-03 리뷰).
      if (this.$route && this.$route.meta && this.$route.meta.layout) return isEmbedRoute(this.$route)
      return isEmbedPath(window.location.pathname)
    },
    /**
     * 로그인 없이 여는 화면인가(`utils/routeAccess.isPublicRoute` — 콜팝 가맹점 가입·휴대폰 서류 올리기). 라우트가 아직 정해지지 않았으면(START —
     * 맞는 레코드가 없다) 주소를 라우터로 풀어 그 레코드의 meta 로 판정한다 — 그러지 않으면 첫 화면의 로그인 이동이 공개 화면을 덮는다(임베드의
     * `isEmbedPath` 와 같은 까닭).
     */
    isPublic() {
      const settled = this.$route && this.$route.matched && this.$route.matched.length > 0
      const route = settled ? this.$route : this.$router.resolve(window.location.pathname).route
      return isPublicRoute(route)
    },
    shouldSubscribeWebsocket() {
      if (this.isEmbed) return false
      if (!this.isLogin) return false
      if (!this.auth.storeId) return false

      if (!this.authId !== 'storeMng' || this.auth.agreeYn) {
        return this.auth.isOrderNoti
      }

      return false
    },
    /**
     * **이 화면의 본문을 만드는가** — 확정된 라우트에 대한 지금의 답(`currentRouteAccess`, 판정은 `utils/routeAccess`)이 허용일
     * 때만. 거절은 가드·`router` 의 감시자가 곧 안내로 바꿔 끼우므로 그 사이에도 본문을 만들지 않는다.
     */
    routeAllowed() {
      return this.$store.getters.currentRouteAccess.allowed
    },
    /** 세션 메뉴를 기다리는 중인가 — 셸은 그리고 본문 자리에 로딩만 둔다 */
    routePending() {
      return this.$store.getters.currentRouteAccess.needsMenu === true
    },
    /** `<router-view>` 의 키 — 라우트 레코드(`utils/routeLayout` 의 [routeViewKey]) */
    viewKey() {
      return routeViewKey(this.$route)
    },
    layoutComponent() {
      if (this.isEmbed) return EmbedLayout
      switch (routeLayoutOf(this.$route)) {
        case ROUTE_LAYOUT.CLEAR:
          return ClearLayout
        case ROUTE_LAYOUT.EMBED:
          return EmbedLayout
        case ROUTE_LAYOUT.DEFAULT:
        default:
          return DefaultLayout
      }
    },
  },

  watch: {
    /**
     * 보류된 화면이 세션 메뉴를 청한다 — 셸의 SideNav 도 같은 것을 청하지만 부르는 것은 `menu/loadMenuList` 하나이고 같은
     * 계정·언어면 한 번이다(`utils/sessionMenu.needsMenuRead`). 셸이 없는 레이아웃에서도 보류가 풀리게 여기서도 청한다.
     */
    routePending: {
      handler(pending) {
        if (pending) this.$store.dispatch('menu/loadMenuList', { locale: this.$i18n.locale })
      },
      immediate: true,
    },
    isLogin: {
      handler(val) {
        if (!val) {
          // 임베드 창에는 로그인 화면이 없다 — 그 화면이 「POS 에서 다시 열어 주세요」로 답한다 (브리프 D2)
          if (this.isEmbed) return
          // 공개 화면(가입·휴대폰 서류 올리기)은 로그인 없이 쓴다
          if (this.isPublic) return
          if (this.$route.path !== '/login') {
            this.$router.push('/login')
          }
        }
      },
      immediate: true,
    },
    shouldSubscribeWebsocket: {
      handler: function (val) {
        if (!val) return
        // 샵인샵 — 대표 매장 로그인 시 그룹 전체 storeIds 구독, 아니면 단일 storeId (하위호환)
        const storeIds = this.auth.storeIds && this.auth.storeIds.length ? this.auth.storeIds : this.auth.storeId
        this.$webSocket.init(storeIds)
      },
      immediate: true,
    },
  },

  mounted() {
    this.$webSocket.addListener(this.handleWebSocket)
  },
  beforeDestroy() {
    this.$webSocket.removeListener(this.handleWebSocket)
  },
  methods: {
    async handleWebSocket(data) {
      console.log('webSocket data: ', data)

      this.$notify({
        title: this.$t('주문 통지'),
        dangerouslyUseHTMLString: true,
        message: data,
        position: 'bottom-right',
        duration: 10000,
      })

      const dingdong = document.getElementById('audio')
      if (dingdong) {
        try {
          await dingdong.play()
        } catch (e) {}
      }
    },
  },
}
</script>

<style scoped>
.route-pending {
  min-height: 240px;
}
</style>
